import React, { useRef, useEffect, useState } from 'react';
import { Box, CircularProgress, TextField } from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import OlcButton from '~/components/General/OlcButton';
import './style.scss';

const InlineEditInput = ({
  value,
  setValue,
  handleSave,
  handleCancel,
  saving = false,
  errorMessage = '',
  maxLength = null,
  shouldCancel = false,
  autoSelect = true,
  inputDynamicWidth = false,
  defaultWidth = 130,
  maxWidth = 320,
  ariaLabel = 'Edit name',
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);
  const [showError, setShowError] = useState(false);
  const [inputWidth, setInputWidth] = useState(defaultWidth);

  // Function to measure text width more accurately
  const measureTextWidth = (text, fontSize = '14px', fontFamily = 'Arial, sans-serif') => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `${fontSize} ${fontFamily}`;
    return context.measureText(text).width;
  };

  // Update local value when external value changes (e.g., when component is reset)
  useEffect(() => {
    setLocalValue(value);
    // Reset error state when value is reset externally (after successful save)
    setShowError(false);
  }, [value]);

  // Initialize width when component mounts
  useEffect(() => {
    const padding = 100; // More generous padding for buttons and spacing
    const maxChars = maxLength || 35;

    if (!inputDynamicWidth) {
      // If dynamic width is disabled, always use default width
      setInputWidth(defaultWidth);
      return;
    }

    if (value.length <= 0) {
      setInputWidth(defaultWidth);
      return;
    }

    // Use actual text measurement for more accurate width
    const textToMeasure = value.substring(0, maxChars);
    const textWidth = measureTextWidth(textToMeasure);

    // Always ensure enough width for the text, but start with default
    const calculatedWidth = Math.max(defaultWidth, textWidth + padding);
    setInputWidth(Math.min(maxWidth, calculatedWidth));
  }, [value, maxLength, inputDynamicWidth, defaultWidth, maxWidth]);

  // Auto-cancel when shouldCancel becomes true (e.g., when modals are opened)
  useEffect(() => {
    if (shouldCancel) {
      setLocalValue(value);
      setShowError(false);
      handleCancel();
    }
  }, [shouldCancel, value, handleCancel]);

  // Check if input exceeds maxLength
  const isOverMaxLength = maxLength && localValue.length > maxLength;
  const isAtMaxLength = maxLength && localValue.length === maxLength;
  const isBlank = (localValue || '').trim().length === 0;
  const currentErrorMessage = isBlank ? 'This field is required.' : errorMessage;

  // Update input width when text changes
  useEffect(() => {
    const padding = 100; // More generous padding for buttons and spacing
    const maxChars = maxLength || 35; // Maximum characters to consider

    if (!inputDynamicWidth) {
      // If dynamic width is disabled, always use default width
      setInputWidth(defaultWidth);
      return;
    }

    if (localValue.length <= 0) {
      setInputWidth(defaultWidth);
      return;
    }

    // Use actual text measurement for more accurate width
    const textToMeasure = localValue.substring(0, maxChars);
    const textWidth = measureTextWidth(textToMeasure);

    // Always ensure enough width for the text, but start with default
    const calculatedWidth = Math.max(defaultWidth, textWidth + padding);
    setInputWidth(Math.min(maxWidth, calculatedWidth));
  }, [localValue, maxLength, inputDynamicWidth, defaultWidth, maxWidth]);

  // Auto focus and select all text when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      if (autoSelect) {
        inputRef.current.select();
      }
    }
  }, [autoSelect]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOverMaxLength || isBlank) {
        setShowError(true);
        return;
      }
      // Update external state with current input value
      setValue(localValue);
      // Pass the current input value directly to handleSave
      handleSave(localValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Reset local value to original value
      setLocalValue(value);
      setShowError(false);
      handleCancel();
    } else {
      // Stop DataGrid from intercepting navigation keys
      e.stopPropagation();
    }
  };

  const handleSaveClick = () => {
    if (isOverMaxLength || isBlank) {
      setShowError(true);
      return;
    }
    // Update external state with current input value
    setValue(localValue);
    // Pass the current input value directly to handleSave
    handleSave(localValue);
  };

  const handleCancelClick = () => {
    // Reset local value to original value
    setLocalValue(value);
    setShowError(false);
    handleCancel();
  };

  return (
    <div>
      <div
        className="inlineEditInputWrapper"
        style={{
          width: `min(100%, ${Math.min(maxWidth, Math.max(inputWidth, defaultWidth))}px)`,
          maxWidth: `${maxWidth}px`,
          display: 'block',
        }}
      >
        <Box
          className="inlineEditWrapper"
          style={{
            width: `min(100%, ${Math.min(maxWidth, Math.max(inputWidth, defaultWidth))}px)`,
            maxWidth: `${maxWidth}px`,
          }}
        >
          <TextField
            inputRef={inputRef}
            type="text"
            value={localValue}
            variant="outlined"
            size="small"
            inputProps={{ 'aria-label': ariaLabel }}
            onChange={(e) => {
              const newValue = e.target.value;

              // If maxLength is set and new value exceeds it, don't update the state
              if (maxLength && newValue.length > maxLength) {
                setShowError(true);
                // return; // Don't update localValue, preventing the input from accepting more text
              }

              setLocalValue(newValue);
              // Show error if empty/whitespace, or over max length; otherwise clear
              const blankNow = (newValue || '').trim().length === 0;
              const overNow = maxLength && newValue.length > maxLength;
              setShowError(blankNow || overNow);
            }}
            onBlur={() => {
              // Validate on blur as well
              const blankNow = (localValue || '').trim().length === 0;
              setShowError(blankNow || isOverMaxLength);
            }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            error={Boolean(isOverMaxLength || isAtMaxLength || (showError && isBlank))}
            className={`inlineEditField ${isOverMaxLength || isAtMaxLength || (showError && isBlank) ? 'error' : ''}`}
          />

          <Box className="editActions">
            <OlcButton
              variant="secondary"
              iconOnly
              type="button"
              aria-label="Cancel edit"
              startIcon={<CloseIcon />}
              onClick={handleCancelClick}
              disabled={saving}
              className="cancelButton"
            />
            <OlcButton
              variant="primary"
              iconOnly
              type="button"
              aria-label="Save edit"
              startIcon={<CheckIcon />}
              onClick={handleSaveClick}
              disabled={saving || isOverMaxLength || isBlank}
              className="saveButton"
            >
              {saving && <CircularProgress sx={{ color: 'currentColor' }} size={16} />}
            </OlcButton>
          </Box>
        </Box>
      </div>
      {/* Error Message - now below the border */}
      {(showError || isOverMaxLength) && currentErrorMessage && (
        <Box className="olcErrorMessage">{currentErrorMessage}</Box>
      )}
    </div>
  );
};

export default InlineEditInput;
